/**
 * Arasyems: 15-minute cart time for one-of-a-kind pieces.
 *
 * <ar-cart-timer> (rendered by snippets/free-shipping-bar.liquid in the drawer and on /cart)
 *   lists the cart lines that are single pieces. The drawer is rendered on every page, so it also
 *   drives a slim countdown bar at the top of the page with a checkout link. Each piece gets a start time in localStorage
 *   the first time it shows up in the cart. When its 15 minutes are over, the line is removed
 *   from the cart, so the countdown always tells the truth.
 */

const STORE_KEY = 'ar_holds';
const FLASH_KEY = 'ar_hold_expired';
const MINUTE = 60 * 1000;

/** @returns {Record<string, number>} variant id -> start time (ms) */
function readHolds() {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

/** @param {Record<string, number>} holds */
function writeHolds(holds) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(holds));
  } catch {}
}

/** @param {number} ms */
function clock(ms) {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/* ---------- toast after an automatic removal ---------- */

/** @param {string} text */
function toast(text) {
  if (!text) return;
  const el = document.createElement('div');
  el.className = 'ar-toast';
  el.setAttribute('role', 'status');
  el.textContent = text;
  document.body.append(el);
  requestAnimationFrame(() => el.classList.add('is-in'));
  setTimeout(() => {
    el.classList.remove('is-in');
    setTimeout(() => el.remove(), 400);
  }, 7000);
}

function showFlash() {
  try {
    const text = sessionStorage.getItem(FLASH_KEY);
    if (!text) return;
    sessionStorage.removeItem(FLASH_KEY);
    toast(text);
  } catch {}
}

let expiring = false;

/**
 * Removes the given cart lines. Uses the platform cart action so the drawer, the cart page and
 * the cart count refresh themselves; falls back to the AJAX API and a reload.
 * @param {{key: string, variant: string}[]} lines
 * @param {string} message
 * @param {string} cartUrl
 */
async function expire(lines, message, cartUrl) {
  if (expiring || !lines.length) return;
  expiring = true;
  const holds = readHolds();
  for (const l of lines) delete holds[l.variant];
  writeHolds(holds);

  const onCartPage = location.pathname.replace(/\/$/, '').endsWith(cartUrl.replace(/\/$/, ''));
  try {
    const actions = window.Shopify?.actions;
    if (actions?.updateCart && !onCartPage) {
      await actions.updateCart({ lines: lines.map((l) => ({ id: l.key, quantity: 0 })) });
      toast(message);
    } else {
      const updates = Object.fromEntries(lines.map((l) => [l.key, 0]));
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      try {
        sessionStorage.setItem(FLASH_KEY, message);
      } catch {}
      location.reload();
    }
  } catch {
    location.reload();
  } finally {
    expiring = false;
  }
}

/* ---------- page-wide countdown bar ---------- */

/** @type {Set<ArCartTimer>} */
const timers = new Set();
/** @type {HTMLElement | null} */
let bar = null;

/** @param {HTMLTemplateElement} tpl @param {number} left @param {boolean} ending */
function showBar(tpl, left, ending) {
  if (!bar) {
    const node = tpl.content.firstElementChild?.cloneNode(true);
    if (!(node instanceof HTMLElement)) return;
    bar = node;
    document.body.prepend(bar);
    document.documentElement.classList.add('ar-has-timebar');
    const setHeight = () =>
      bar && document.documentElement.style.setProperty('--ar-timebar-h', `${bar.offsetHeight}px`);
    setHeight();
    new ResizeObserver(setHeight).observe(bar);
  }
  const el = bar.querySelector('[data-ar-clock]');
  if (el) el.textContent = clock(left);
  bar.classList.toggle('is-ending', ending);
}

function hideBarIfIdle() {
  // Section re-renders swap the timer element; wait a moment before deciding it is gone.
  setTimeout(() => {
    if ([...timers].some((t) => t.isConnected && t.lines.length)) return;
    bar?.remove();
    bar = null;
    document.documentElement.classList.remove('ar-has-timebar');
    document.documentElement.style.removeProperty('--ar-timebar-h');
  }, 400);
}

class ArCartTimer extends HTMLElement {
  static observedAttributes = ['data-lines'];

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #interval;
  #lastMinute = -1;

  connectedCallback() {
    timers.add(this);
    this.#sync();
    this.#interval = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.#interval);
    timers.delete(this);
    hideBarIfIdle();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#sync();
  }

  /** @returns {{key: string, variant: string}[]} */
  get lines() {
    return (this.dataset.lines || '')
      .split(',')
      .filter(Boolean)
      .map((pair) => {
        const [key, variant] = pair.split('|');
        return { key, variant };
      });
  }

  get duration() {
    return (Number(this.dataset.minutes) || 15) * MINUTE;
  }

  #sync() {
    const holds = readHolds();
    const now = Date.now();
    const current = new Set();
    for (const { variant } of this.lines) {
      current.add(variant);
      if (!holds[variant] || now - holds[variant] > this.duration + MINUTE) {
        holds[variant] = now;
      }
    }
    // The element lists every single piece in the cart, so anything else is no longer held.
    for (const variant of Object.keys(holds)) {
      if (!current.has(variant)) {
        delete holds[variant];
      }
    }
    writeHolds(holds);
    this.#tick();
  }

  #tick() {
    const holds = readHolds();
    const now = Date.now();
    const lines = this.lines;
    if (!lines.length) {
      hideBarIfIdle();
      return;
    }
    const left = Math.min(...lines.map((l) => (holds[l.variant] ?? now) + this.duration - now));

    const live = this.querySelector('.ar-cartx__timer-live');
    const plain = this.querySelector('.ar-cartx__unique--static');
    if (live instanceof HTMLElement && plain instanceof HTMLElement) {
      live.hidden = false;
      plain.hidden = true;
    }
    const el = this.querySelector('[data-ar-clock]');
    if (el) el.textContent = clock(left);
    this.classList.toggle('is-ending', left < 2 * MINUTE);
    const tpl = this.querySelector('template[data-ar-bar]');
    if (tpl instanceof HTMLTemplateElement && left > 0) showBar(tpl, left, left < 2 * MINUTE);

    const minute = Math.ceil(left / MINUTE);
    if (minute !== this.#lastMinute) {
      this.#lastMinute = minute;
      const main = this.querySelector('.ar-cartx__timer-main');
      if (main) main.setAttribute('aria-label', (main.textContent || '').replace(/\d+:\d{2}/, `${minute} min`));
    }

    if (left <= 0) {
      hideBarIfIdle();
      const due = lines.filter((l) => (holds[l.variant] ?? now) + this.duration <= now);
      expire(due, this.dataset.expired || '', this.dataset.cartUrl || '/cart');
    }
  }
}

if (!customElements.get('ar-cart-timer')) customElements.define('ar-cart-timer', ArCartTimer);
showFlash();
